import { useState, useRef, useCallback, memo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { 
  MessageSquare, 
  Edit, 
  Trash2, 
  ChevronDown,
  Save,
  X,
  Reply,
  CornerDownRight
} from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { DeleteConfirmationModal } from "./DeleteConfirmationModal";

interface Comment {
  id: string;
  user: {
    id: string;
    name: string;
    role: string;
    avatar?: string;
  };
  datetime: string;
  text: string;
  canEdit: boolean;
  parentId?: string;
  replies?: Comment[];
}

interface CommentsSectionProps {
  comments: Comment[];
  reportStatus: "draft" | "in_review" | "signed" | "approved";
  currentUserId?: string;
  onAddComment?: (text: string, parentId?: string) => void;
  onEditComment?: (commentId: string, newText: string) => void;
  onDeleteComment?: (commentId: string) => void;
}

const quickReactions = [];

// Component to render individual comment with replies
const CommentItem = memo(({ 
  comment, 
  isReply = false,
  currentUserId,
  canEditComments,
  editingComment,
  editText,
  replyingTo,
  replyText,
  editTextareaRef,
  replyTextareaRef,
  onEditComment,
  onSaveEdit,
  onCancelEdit,
  onReplyClick,
  onSaveReply,
  onCancelReply,
  onDeleteClick,
  setEditText,
  setReplyText
}: { 
  comment: Comment; 
  isReply?: boolean;
  currentUserId: string;
  canEditComments: boolean;
  editingComment: string | null;
  editText: string;
  replyingTo: string | null;
  replyText: string;
  editTextareaRef: React.RefObject<HTMLTextAreaElement>;
  replyTextareaRef: React.RefObject<HTMLTextAreaElement>;
  onEditComment: (commentId: string, currentText: string) => void;
  onSaveEdit: () => void;
  onCancelEdit: () => void;
  onReplyClick: (commentId: string) => void;
  onSaveReply: () => void;
  onCancelReply: () => void;
  onDeleteClick: (commentId: string) => void;
  setEditText: (text: string) => void;
  setReplyText: (text: string) => void;
}) => {
  const shouldShowAllComments = typeof window !== "undefined" && window.matchMedia?.("print")?.matches;
  
  return (
    <div className={`${isReply ? 'ml-8 border-l-2 border-muted pl-4' : ''} space-y-2`}>
      <div className="flex gap-2 p-2 border-l-2 border-primary/20 bg-card/30 rounded-r print-no-break print-flat">
        {isReply && (
          <CornerDownRight className="w-4 h-4 text-muted-foreground mt-1 flex-shrink-0" />
        )}
        <Avatar className="w-6 h-6 flex-shrink-0">
          <AvatarImage src={comment.user.avatar} />
          <AvatarFallback className="text-xs">{comment.user.name.charAt(0)}</AvatarFallback>
        </Avatar>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1 mb-1">
            <span className="font-medium text-xs print-text">{comment.user.name}</span>
            <Badge variant="outline" className="text-xs px-1 py-0 print-badge">{comment.user.role}</Badge>
            <span className="text-xs text-muted-foreground print-text">
              {format(new Date(comment.datetime), "dd/MM/yyyy HH:mm", { locale: ptBR })}
            </span>
            
            {/* Action buttons */}
            {!shouldShowAllComments && (
              <div className="flex gap-1 ml-auto">
                {/* Reply button - available for everyone when report not approved */}
                {canEditComments && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-5 w-5 p-0"
                    onClick={() => onReplyClick(comment.id)}
                    title="Responder comentário"
                  >
                    <Reply className="w-3 h-3" />
                  </Button>
                )}
                
                {/* Edit/Delete buttons for author only */}
                {comment.user.id === currentUserId && canEditComments && (
                  <>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-5 w-5 p-0"
                      onClick={() => onEditComment(comment.id, comment.text)}
                      title="Editar comentário"
                    >
                      <Edit className="w-3 h-3" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-5 w-5 p-0 text-destructive"
                      onClick={() => onDeleteClick(comment.id)}
                      title="Excluir comentário"
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </>
                )}
              </div>
            )}
          </div>
          
          {/* Comment text or edit form */}
          {editingComment === comment.id ? (
            <div className="space-y-2">
              <Textarea
                ref={editTextareaRef}
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                className="text-xs min-h-12"
              />
              <div className="flex gap-1">
                <Button size="sm" onClick={onSaveEdit} className="text-xs h-6">
                  <Save className="w-3 h-3 mr-1" />
                  Salvar
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={onCancelEdit}
                  className="text-xs h-6"
                >
                  <X className="w-3 h-3 mr-1" />
                  Cancelar
                </Button>
              </div>
            </div>
          ) : (
            <p className="text-xs leading-relaxed print-text">{comment.text}</p>
          )}
        </div>
      </div>

      {/* Reply form */}
      {replyingTo === comment.id && (
        <div className="ml-8 p-3 border rounded-lg bg-muted/50">
          <div className="space-y-2">
            <Textarea
              ref={replyTextareaRef}
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              placeholder="Digite sua resposta..."
              className="text-xs min-h-12"
            />
            <div className="flex gap-1">
              <Button 
                size="sm" 
                onClick={onSaveReply} 
                className="text-xs h-6"
                disabled={!replyText.trim()}
              >
                <Reply className="w-3 h-3 mr-1" />
                Responder
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={onCancelReply}
                className="text-xs h-6"
              >
                <X className="w-3 h-3 mr-1" />
                Cancelar
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Render replies */}
      {comment.replies && comment.replies.length > 0 && (
        <div className="space-y-2">
          {comment.replies.map((reply) => (
            <CommentItem 
              key={reply.id} 
              comment={reply} 
              isReply 
              currentUserId={currentUserId}
              canEditComments={canEditComments}
              editingComment={editingComment}
              editText={editText}
              replyingTo={replyingTo}
              replyText={replyText}
              editTextareaRef={editTextareaRef}
              replyTextareaRef={replyTextareaRef}
              onEditComment={onEditComment}
              onSaveEdit={onSaveEdit}
              onCancelEdit={onCancelEdit}
              onReplyClick={onReplyClick}
              onSaveReply={onSaveReply}
              onCancelReply={onCancelReply}
              onDeleteClick={onDeleteClick}
              setEditText={setEditText}
              setReplyText={setReplyText}
            />
          ))}
        </div>
      )}
    </div>
  );
});

CommentItem.displayName = "CommentItem";

export function CommentsSection({ 
  comments, 
  reportStatus, 
  currentUserId = 'current-user',
  onAddComment,
  onEditComment,
  onDeleteComment
}: CommentsSectionProps) {
  const [editingComment, setEditingComment] = useState<string | null>(null);
  const [editText, setEditText] = useState("");
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyText, setReplyText] = useState("");
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [commentToDelete, setCommentToDelete] = useState<string | null>(null);
  const timelineRef = useRef<HTMLDivElement>(null);
  const replyTextareaRef = useRef<HTMLTextAreaElement>(null);
  const editTextareaRef = useRef<HTMLTextAreaElement>(null);

  const canEditComments = reportStatus === "draft" || reportStatus === "in_review";

  // Organize comments hierarchically
  const organizeComments = (comments: Comment[]): Comment[] => {
    const commentMap = new Map<string, Comment>();
    const rootComments: Comment[] = [];

    // First pass: create map and initialize replies array
    comments.forEach(comment => {
      commentMap.set(comment.id, { ...comment, replies: [] });
    });

    // Second pass: organize hierarchy
    comments.forEach(comment => {
      const commentWithReplies = commentMap.get(comment.id)!;
      
      if (comment.parentId) {
        const parent = commentMap.get(comment.parentId);
        if (parent) {
          parent.replies!.push(commentWithReplies);
        }
      } else {
        rootComments.push(commentWithReplies);
      }
    });

    return rootComments;
  };

  const organizedComments = organizeComments(comments);

  const handleEditComment = useCallback((commentId: string, currentText: string) => {
    setEditingComment(commentId);
    setEditText(currentText);
  }, []);

  const handleSaveEdit = useCallback(() => {
    if (editingComment && onEditComment) {
      onEditComment(editingComment, editText);
    }
    setEditingComment(null);
    setEditText("");
  }, [editingComment, editText, onEditComment]);

  const handleCancelEdit = useCallback(() => {
    setEditingComment(null);
    setEditText("");
  }, []);

  const handleDeleteClick = useCallback((commentId: string) => {
    setCommentToDelete(commentId);
    setDeleteConfirmOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (commentToDelete && onDeleteComment) {
      onDeleteComment(commentToDelete);
    }
    setCommentToDelete(null);
  }, [commentToDelete, onDeleteComment]);

  const handleReplyClick = useCallback((commentId: string) => {
    setReplyingTo(commentId);
    setReplyText("");
  }, []);

  const handleSaveReply = useCallback(() => {
    if (replyingTo && onAddComment && replyText.trim()) {
      onAddComment(replyText, replyingTo);
      setReplyingTo(null);
      setReplyText("");
    }
  }, [replyingTo, replyText, onAddComment]);

  const handleCancelReply = useCallback(() => {
    setReplyingTo(null);
    setReplyText("");
  }, []);

  const scrollToBottom = useCallback(() => {
    if (timelineRef.current) {
      timelineRef.current.scrollTop = timelineRef.current.scrollHeight;
    }
  }, []);

  // Em modo impressão, sempre renderize TODOS os comentários (sem scroll limitado)
  const shouldShowAllComments = typeof window !== "undefined" && window.matchMedia?.("print")?.matches;

  return (
    <>
      <DeleteConfirmationModal
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        onConfirm={handleConfirmDelete}
      />

      <div className="space-y-4">
        {comments.length === 0 ? (
          <div className="text-center text-muted-foreground py-8">
            Nenhum comentário adicionado
          </div>
        ) : (
          <div 
            ref={timelineRef}
            className={`space-y-4 ${shouldShowAllComments ? 'print-comments-full' : 'max-h-96 overflow-y-auto'} print-expand`}
          >
            {organizedComments.map((comment) => (
              <CommentItem 
                key={comment.id} 
                comment={comment}
                currentUserId={currentUserId}
                canEditComments={canEditComments}
                editingComment={editingComment}
                editText={editText}
                replyingTo={replyingTo}
                replyText={replyText}
                editTextareaRef={editTextareaRef}
                replyTextareaRef={replyTextareaRef}
                onEditComment={handleEditComment}
                onSaveEdit={handleSaveEdit}
                onCancelEdit={handleCancelEdit}
                onReplyClick={handleReplyClick}
                onSaveReply={handleSaveReply}
                onCancelReply={handleCancelReply}
                onDeleteClick={handleDeleteClick}
                setEditText={setEditText}
                setReplyText={setReplyText}
              />
            ))}
          </div>
        )}

        {comments.length > 3 && !shouldShowAllComments && (
          <div className="flex justify-center">
            <Button
              variant="outline"
              size="sm"
              onClick={scrollToBottom}
              className="text-xs"
            >
              <ChevronDown className="w-4 h-4 mr-1" />
              Ir ao mais recente
            </Button>
          </div>
        )}
      </div>
    </>
  );
}
